import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MessagingService } from "../../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../../core/messaging/severity-type.enum";
import { UserService } from "../../../../../../core/user/user.service";
import { FormService } from "../../../../../../dynamic-forms/form.service";
import { Dropdown } from "../../../../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { SelectableInput } from "../../../../../../dynamic-forms/inputs/selectable-input.model";
import { NumberHelper } from "../../../../../../utilities/contracts/number-helper";
import { StringHelper } from "../../../../../../utilities/contracts/string-helper";
import { ProjectType } from "../../../../project/project-type.enum";
import { ChaseDetailForAuditItem } from "../../../chase-detail-for-audit-item.model";
import { AuditDataSharingService } from "../../../services/audit-data-sharing.service";
import { AuditPackageGenerateService } from "../../audit-query-quality/audit-package-generate/audit-package-generate.service";
import { AuditQueryGridService } from "../../audit-query-quality/audit-query-grid/audit-query-grid.service";
import { AuditPackageGenerateRiskService } from "../../audit-query-risk/audit-package-generate-risk/audit-package-generate-risk.service";
import { AuditQueryGridRiskService } from "../../audit-query-risk/audit-query-grid-risk/audit-query-grid-risk.service";
import { AuditQueryGridFiltersService } from "../audit-query-grid-filters/audit-query-grid-filters.service";
import { IAuditQueryGridData } from "../audit-query-grid-filters/member-radv-record.model";

@Component({
  selector: "app-audit-query-item-readiness",
  templateUrl: "./audit-query-item-readiness.component.html",
  styleUrls: ["./audit-query-item-readiness.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditQueryItemReadinessComponent implements OnInit {
  form: FormGroup;
  @Output() onStatusChange: EventEmitter<any> = new EventEmitter(null);
  @Input() chaseDetailsForAudit: IAuditQueryGridData;
  @Input() projectType: number = ProjectType.HEDIS;
  auditReadyInput: Dropdown;
  showConfirmationForAutoDeleteAuditEntry = false;
  newAuditReadyStatus = "";

  constructor(
    protected readonly formService: FormService,
    private auditQueryGridService: AuditQueryGridService,
    private auditPackageGenerateService: AuditPackageGenerateService,
    private auditQueryGridRiskService: AuditQueryGridRiskService,
    private auditPackageGenerateRiskService: AuditPackageGenerateRiskService,
    private auditQueryGridFiltersService: AuditQueryGridFiltersService,
    private auditDataSharingService: AuditDataSharingService,
    private messagingService: MessagingService,
    private changeDetector: ChangeDetectorRef,
    private userService: UserService
  ) { }

  ngOnInit(): void {

    this.auditReadyInput = new Dropdown({
      key: "auditReady",
      appendTo: "body",
      autoDisplayFirst: false,
      disabled : this.chaseDetailsForAudit.isAuditPackageLocked,
      options: [
        new SelectableInput({ text: "", value: "blank", extra: "blank" }),
        new SelectableInput({ text: "Yes", value: "yes", extra: true }),
        new SelectableInput({ text: "No", value: "no", extra: false }),
      ],
    });
    this.form = this.formService.createFormGroup([this.auditReadyInput]);
    this.form.get(this.auditReadyInput.key).setValue(this.chaseDetailsForAudit.auditReady);

  }

  auditReadyStatusChange($event: SelectableInput): void {
    this.newAuditReadyStatus = $event.value.toString();

    if (this.newAuditReadyStatus.toString().toLowerCase() !== "yes"
      && (this.chaseDetailsForAudit.action.toLowerCase() === "edit" || this.chaseDetailsForAudit.action.toLowerCase() === "select")) {

      // For risk projects, check if there are audit packages with pages selected
      if (this.projectType === ProjectType.RISK) {
        if (this.hasAuditPackagesWithPages()) {
          this.showConfirmationForAutoDeleteAuditEntry = true;
          return;
        }
      } else {
        // For HEDIS, show confirmation only for chases that have an audit package item
        if (NumberHelper.isGreaterThan(Number(this.chaseDetailsForAudit.auditPackageItemId), 0)) {
          this.showConfirmationForAutoDeleteAuditEntry = true;
          return;
        }
      }
    }
    this.updateAuditReadyStatus(this.newAuditReadyStatus);
  }

  private updateAuditReadyStatus(auditReady: string): void {
    const currentUser = this.userService.getUserToken();
    const currentUserName = currentUser ? `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim() || currentUser.username : "Unknown User";

    if (this.projectType === ProjectType.RISK && !this.chaseDetailsForAudit.entityId) {
      this.messagingService.showToast("Entity ID is required for updating audit ready status.", SeverityType.ERROR);
      return;
    }

    const auditPackageIds = [];
    if (NumberHelper.isAvailable(this.chaseDetailsForAudit.auditPackageId)) {
      auditPackageIds.push(this.chaseDetailsForAudit.auditPackageId);
    }

    const auditReadyValue = StringHelper.isAvailable(auditReady) && auditReady !== "blank" ? auditReady : null;

    const updateAuditReadyState = new ChaseDetailForAuditItem({
      auditPackageIds,
      projectId: this.chaseDetailsForAudit.projectId,
      chaseId: this.chaseDetailsForAudit.chaseId,
      numeratorId: this.chaseDetailsForAudit.numeratorId,
      entityId: this.projectType === ProjectType.RISK ? this.chaseDetailsForAudit.entityId : undefined,
      auditReady: auditReadyValue,
      icdCode: this.chaseDetailsForAudit.icdCode,
      hccCode: this.chaseDetailsForAudit.hccCode,
      cmsModel: this.chaseDetailsForAudit.cmsModel,
      memberId: this.projectType === ProjectType.RISK ? this.chaseDetailsForAudit.memberId : undefined,
      memberDOB: this.chaseDetailsForAudit.memberDOB,
      memberFirstName: this.chaseDetailsForAudit.memberFirstName,
      memberLastName: this.chaseDetailsForAudit.memberLastName,
    });

    if (this.projectType === ProjectType.RISK) {
      this.auditQueryGridRiskService.saveAuditReadyStatus(updateAuditReadyState).subscribe(
        data => {
          const updatedData = {
            chaseId: this.chaseDetailsForAudit.chaseId,
            entityId: this.chaseDetailsForAudit.entityId,
            auditReady: auditReadyValue,
            auditReadyUpdateUser: StringHelper.isAvailable(auditReadyValue) ? currentUserName : null,
          };
          this.auditQueryGridRiskService.sendMemberRadvCardData(data);
          this.auditQueryGridRiskService.reloadAuditGridFromState(updatedData);
          this.changeDetector.markForCheck();
        },
        err => {
          this.messagingService.showToast("Failed to update Audit Ready Status.", SeverityType.ERROR);
          this.changeDetector.markForCheck();
        }
      );
    } else {
      this.auditQueryGridService.saveAuditReadyStatus(updateAuditReadyState).subscribe(
        data => {
          const updatedData = {
            chaseId: this.chaseDetailsForAudit.chaseId,
            numeratorId: this.chaseDetailsForAudit.numeratorId,
            auditReady: auditReadyValue,
            auditReadyUpdateUser: auditReadyValue === null ? null : currentUserName,
          };
          this.auditQueryGridService.reloadAuditGridFromState(updatedData);
          this.changeDetector.markForCheck();
        },
        err => {
          this.messagingService.showToast("Failed to update Audit Ready Status.", SeverityType.ERROR);
          this.changeDetector.markForCheck();
        }
      );
    }
  }

  onChangeStatusConfirm(): void {
    this.showConfirmationForAutoDeleteAuditEntry = false;
    this.updateAuditReadyStatus(this.newAuditReadyStatus);
    const auditPackageItemId = NumberHelper.toNumber(this.chaseDetailsForAudit.auditPackageItemId);

    setTimeout(() => {
      if (this.projectType === ProjectType.RISK) {
        this.auditPackageGenerateRiskService.autoDeleteAuditPackageItem(auditPackageItemId);
      } else {
        this.auditPackageGenerateService.autoDeleteAuditPackageItem(auditPackageItemId);
      }
    },         200);
  }

  onCancelStatusChange(): void {
    this.form.get(this.auditReadyInput.key).setValue(this.chaseDetailsForAudit.auditReady);
  }

  private hasAuditPackagesWithPages(): boolean {
    const auditPackages = this.auditDataSharingService.getAuditPackages();

    if (!auditPackages || auditPackages.length === 0) {
      return false;
    }

    for (const auditPackage of auditPackages) {
      const pageEntries = this.auditDataSharingService.getPageEntries(auditPackage.auditPackageID);

      if (pageEntries && pageEntries.length > 0) {
        const hasPagesWithData = pageEntries.some(pageEntry =>
          pageEntry.riskPageData &&
          pageEntry.pageEntries &&
          pageEntry.pageEntries.length > 0 &&
          (pageEntry.chaseId === this.chaseDetailsForAudit.chaseId ||
           pageEntry.riskPageData.entityId === this.chaseDetailsForAudit.entityId)
        );

        if (hasPagesWithData) {
          return true;
        }
      }
    }

    return false;
  }
}
